package com.snakesworld.game.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.snakesworld.game.engine.SNAKE_SKIN_OPTIONS
import com.snakesworld.game.engine.SnakeSkinOption
import com.snakesworld.game.ui.theme.LocalGameCanvasColors
import com.snakesworld.game.ui.theme.appFontFamily
import com.snakesworld.game.viewmodel.MenuViewModel
import org.jetbrains.compose.resources.stringResource
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.enter_nickname
import snakesworld.shared.generated.resources.games_played_label
import snakesworld.shared.generated.resources.high_score_label
import snakesworld.shared.generated.resources.leaderboard
import snakesworld.shared.generated.resources.multiplayer
import snakesworld.shared.generated.resources.single_player
import snakesworld.shared.generated.resources.nickname_not_allowed
import snakesworld.shared.generated.resources.best_kills_label
import snakesworld.shared.generated.resources.best_length_label
import snakesworld.shared.generated.resources.nickname
import snakesworld.shared.generated.resources.random_nickname
import snakesworld.shared.generated.resources.select_skin
import snakesworld.shared.generated.resources.start_game

@Composable
fun MainMenuScreen(
    viewModel: MenuViewModel,
    onStartGame: (userId: String, callsign: String, skinId: String) -> Unit,
    onNavigateLeaderboard: () -> Unit,
    onBackToHub: (() -> Unit)? = null,
    isSinglePlayerMode: Boolean = false,
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val canvasColors = LocalGameCanvasColors.current

    LaunchedEffect(Unit) {
        viewModel.loadProfile()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(canvasColors.backgroundColor)
            .windowInsetsPadding(WindowInsets.safeDrawing)
            .padding(16.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                // Keep the title below the top-left back button (same clearance as Settings / Leaderboard).
                .padding(top = if (onBackToHub != null) 44.dp else 0.dp)
        ) {
            MenuHeader(
                isSinglePlayerMode = isSinglePlayerMode,
                canvasColors = canvasColors,
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(modifier = Modifier.height(16.dp))

            BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
                val isWide = maxWidth > 720.dp

                if (isWide) {
                    // Responsive Widescreen Two-Pane Layout
                    Row(
                        modifier = Modifier.fillMaxSize(),
                        horizontalArrangement = Arrangement.spacedBy(32.dp),
                        verticalAlignment = Alignment.Top
                    ) {
                        Column(
                            modifier = Modifier.weight(1f),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            SkinPreviewCard(uiState.selectedSkin, canvasColors)
                            Spacer(modifier = Modifier.height(24.dp))
                            StartHeroButton(
                                enabled = uiState.isValidCallsign,
                                onClick = { onStartGame(uiState.profile.uid, uiState.callsign, uiState.selectedSkin.id) },
                                canvasColors = canvasColors
                            )
                        }

                        Column(
                            modifier = Modifier.weight(1.1f).verticalScroll(rememberScrollState()),
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            CallsignInputSection(
                                callsign = uiState.callsign,
                                isValid = uiState.isValidCallsign,
                                onChanged = viewModel::onCallsignChanged,
                                onRandomize = viewModel::randomizeCallsign,
                                canvasColors = canvasColors
                            )

                            SkinSelectorSection(
                                selectedSkin = uiState.selectedSkin,
                                onSelectSkin = viewModel::onSkinSelected,
                                canvasColors = canvasColors
                            )

                            ArcadeStatsCard(
                                bestScore = if (isSinglePlayerMode) uiState.profile.spBestScore else uiState.profile.bestScore,
                                gamesPlayed = if (isSinglePlayerMode) uiState.profile.spGamesPlayed else uiState.profile.gamesPlayed,
                                bestKills = if (isSinglePlayerMode) uiState.profile.spBestKills else null,
                                bestLength = if (isSinglePlayerMode) uiState.profile.spBestLength else null,
                                canvasColors = canvasColors
                            )

                            if (!isSinglePlayerMode) {
                                LeaderboardNavButton(
                                    onLeaderboard = onNavigateLeaderboard,
                                    canvasColors = canvasColors
                                )
                            }
                        }
                    }
                } else {
                    // Mobile Single-Pane Column Layout
                    Column(
                        modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        SkinPreviewCard(uiState.selectedSkin, canvasColors)

                        CallsignInputSection(
                            callsign = uiState.callsign,
                            isValid = uiState.isValidCallsign,
                            onChanged = viewModel::onCallsignChanged,
                            onRandomize = viewModel::randomizeCallsign,
                            canvasColors = canvasColors
                        )

                        SkinSelectorSection(
                            selectedSkin = uiState.selectedSkin,
                            onSelectSkin = viewModel::onSkinSelected,
                            canvasColors = canvasColors
                        )

                        StartHeroButton(
                            enabled = uiState.isValidCallsign,
                            onClick = { onStartGame(uiState.profile.uid, uiState.callsign, uiState.selectedSkin.id) },
                            canvasColors = canvasColors
                        )

                        ArcadeStatsCard(
                            bestScore = if (isSinglePlayerMode) uiState.profile.spBestScore else uiState.profile.bestScore,
                            gamesPlayed = if (isSinglePlayerMode) uiState.profile.spGamesPlayed else uiState.profile.gamesPlayed,
                            bestKills = if (isSinglePlayerMode) uiState.profile.spBestKills else null,
                            bestLength = if (isSinglePlayerMode) uiState.profile.spBestLength else null,
                            canvasColors = canvasColors
                        )

                        if (!isSinglePlayerMode) {
                            LeaderboardNavButton(
                                onLeaderboard = onNavigateLeaderboard,
                                canvasColors = canvasColors
                            )
                        }

                        Spacer(modifier = Modifier.height(16.dp))
                    }
                }
            }
        }

        if (onBackToHub != null) {
            ScreenBackButton(
                onBack = onBackToHub,
                modifier = Modifier.align(Alignment.TopStart),
                canvasColors = canvasColors,
            )
        }
    }
}

@Composable
private fun MenuHeader(
    isSinglePlayerMode: Boolean,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = stringResource(if (isSinglePlayerMode) Res.string.single_player else Res.string.multiplayer),
            color = canvasColors.hudTextColor,
            fontSize = 24.sp,
            fontWeight = FontWeight.Black,
            fontFamily = appFontFamily(),
            letterSpacing = 2.sp
        )
    }
}

@Composable
private fun CallsignInputSection(
    callsign: String,
    isValid: Boolean,
    onChanged: (String) -> Unit,
    onRandomize: () -> Unit,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = stringResource(Res.string.nickname),
            color = canvasColors.hudTextColor,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
            fontFamily = appFontFamily()
        )
        Spacer(modifier = Modifier.height(4.dp))
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            OutlinedTextField(
                value = callsign,
                onValueChange = onChanged,
                placeholder = {
                    Text(
                        stringResource(Res.string.enter_nickname),
                        fontFamily = appFontFamily(),
                        fontSize = 14.sp
                    )
                },
                singleLine = true,
                isError = callsign.isNotBlank() && !isValid,
                // Keep supportingText off the TextField — Material reserves that slot and
                // mis-aligns the RANDOM button. Show the error under the row instead.
                modifier = Modifier.weight(1f),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = canvasColors.hudTextColor,
                    unfocusedBorderColor = canvasColors.gridColor,
                    focusedTextColor = canvasColors.hudTextColor,
                    unfocusedTextColor = canvasColors.hudTextColor,
                    errorBorderColor = canvasColors.boundaryColor,
                    errorTextColor = canvasColors.hudTextColor,
                )
            )
            Spacer(modifier = Modifier.width(8.dp))
            Button(
                onClick = onRandomize,
                colors = ButtonDefaults.buttonColors(
                    containerColor = canvasColors.hudBgColor,
                    contentColor = canvasColors.hudTextColor,
                ),
                shape = RoundedCornerShape(4.dp),
                modifier = Modifier.border(1.dp, canvasColors.hudTextColor, RoundedCornerShape(4.dp))
            ) {
                Text(
                    text = stringResource(Res.string.random_nickname),
                    color = canvasColors.hudTextColor,
                    fontFamily = appFontFamily(),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold
                )
            }
        }
        if (callsign.isNotBlank() && !isValid) {
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = stringResource(Res.string.nickname_not_allowed),
                color = canvasColors.boundaryColor,
                fontFamily = appFontFamily(),
                fontSize = 11.sp,
            )
        }
    }
}

@Composable
private fun SkinSelectorSection(
    selectedSkin: SnakeSkinOption,
    onSelectSkin: (String) -> Unit,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = stringResource(Res.string.select_skin),
            color = canvasColors.hudTextColor,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
            fontFamily = appFontFamily()
        )
        Spacer(modifier = Modifier.height(8.dp))
        LazyRow(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            items(SNAKE_SKIN_OPTIONS) { skin ->
                val isSelected = skin.id == selectedSkin.id
                val swatchShape = RoundedCornerShape(8.dp)
                val interactionSource = remember { MutableInteractionSource() }
                Box(
                    modifier = Modifier
                        .size(48.dp)
                        .clip(swatchShape)
                        .background(Color(skin.primaryColor), swatchShape)
                        .border(
                            width = if (isSelected) 3.dp else 1.dp,
                            color = if (isSelected) canvasColors.accentColor else canvasColors.gridColor,
                            shape = swatchShape
                        )
                        .clickable(
                            interactionSource = interactionSource,
                            indication = ripple(bounded = true, color = Color.White),
                            onClick = { onSelectSkin(skin.id) }
                        ),
                    contentAlignment = Alignment.Center
                ) {
                    if (isSelected) {
                        Box(
                            modifier = Modifier
                                .size(14.dp)
                                .background(Color.White, RoundedCornerShape(3.dp))
                                .border(1.dp, Color.Black.copy(alpha = 0.5f), RoundedCornerShape(3.dp))
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun SkinPreviewCard(
    skin: SnakeSkinOption,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(130.dp)
            .background(canvasColors.hudBgColor, RoundedCornerShape(12.dp))
            .border(2.dp, canvasColors.boundaryColor, RoundedCornerShape(12.dp)),
        contentAlignment = Alignment.Center
    ) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            val centerY = size.height / 2f

            // Maximum bounds with padding
            val availableWidth = size.width - 32.dp.toPx()
            val availableHeight = size.height - 24.dp.toPx()

            // Desired base dimensions (substantially larger than original 24px)
            val baseHeadSize = 44.dp.toPx()
            val baseSegSize = 38.dp.toPx()
            val baseSpacing = 42.dp.toPx()
            val baseTotalWidth = 4 * baseSpacing + (baseSegSize + baseHeadSize) / 2f

            val scale = minOf(1f, availableWidth / baseTotalWidth, availableHeight / baseHeadSize)

            val headSize = baseHeadSize * scale
            val segSize = baseSegSize * scale
            val spacing = baseSpacing * scale
            val totalWidth = 4 * spacing + (segSize + headSize) / 2f
            val startX = (size.width - totalWidth) / 2f

            val primary = Color(skin.primaryColor)
            val segGlowHalf = (segSize / 2f) * 1.25f
            val headGlowHalf = (headSize / 2f) * 1.25f

            // Draw 4 body segments (retro squares with neon glow)
            for (i in 0 until 4) {
                val segCenterX = startX + segSize / 2f + i * spacing

                // Outer neon glow
                drawRect(
                    color = primary.copy(alpha = 0.25f),
                    topLeft = Offset(segCenterX - segGlowHalf, centerY - segGlowHalf),
                    size = Size(segGlowHalf * 2f, segGlowHalf * 2f)
                )
                // Solid segment
                drawRect(
                    color = primary,
                    topLeft = Offset(segCenterX - segSize / 2f, centerY - segSize / 2f),
                    size = Size(segSize, segSize)
                )
                // Segment outline
                drawRect(
                    color = Color.Black.copy(alpha = 0.35f),
                    topLeft = Offset(segCenterX - segSize / 2f, centerY - segSize / 2f),
                    size = Size(segSize, segSize),
                    style = androidx.compose.ui.graphics.drawscope.Stroke(2f * scale)
                )
            }

            // Head segment (5th segment, length = 5)
            val headCenterX = startX + segSize / 2f + 4 * spacing

            // Outer neon glow for head
            drawRect(
                color = primary.copy(alpha = 0.4f),
                topLeft = Offset(headCenterX - headGlowHalf, centerY - headGlowHalf),
                size = Size(headGlowHalf * 2f, headGlowHalf * 2f)
            )
            // Solid head
            drawRect(
                color = primary,
                topLeft = Offset(headCenterX - headSize / 2f, centerY - headSize / 2f),
                size = Size(headSize, headSize)
            )
            // Head crisp white border
            drawRect(
                color = Color.White,
                topLeft = Offset(headCenterX - headSize / 2f, centerY - headSize / 2f),
                size = Size(headSize, headSize),
                style = androidx.compose.ui.graphics.drawscope.Stroke(2.5f * scale)
            )

            // Retro eyes on the head directed forward (rightwards)
            val eyeHalf = 5.5f * scale
            val eyeForwardOffset = headSize * 0.22f
            val eyeLateralOffset = headSize * 0.24f

            val leftEye = Offset(headCenterX + eyeForwardOffset, centerY - eyeLateralOffset)
            val rightEye = Offset(headCenterX + eyeForwardOffset, centerY + eyeLateralOffset)

            // Eye whites
            drawRect(
                color = Color.White,
                topLeft = Offset(leftEye.x - eyeHalf, leftEye.y - eyeHalf),
                size = Size(eyeHalf * 2f, eyeHalf * 2f)
            )
            drawRect(
                color = Color.White,
                topLeft = Offset(rightEye.x - eyeHalf, rightEye.y - eyeHalf),
                size = Size(eyeHalf * 2f, eyeHalf * 2f)
            )

            // Pupils (looking forward/right)
            val pupilHalf = 2.8f * scale
            val pupilShift = 2.2f * scale
            drawRect(
                color = Color.Black,
                topLeft = Offset(leftEye.x + pupilShift - pupilHalf, leftEye.y - pupilHalf),
                size = Size(pupilHalf * 2f, pupilHalf * 2f)
            )
            drawRect(
                color = Color.Black,
                topLeft = Offset(rightEye.x + pupilShift - pupilHalf, rightEye.y - pupilHalf),
                size = Size(pupilHalf * 2f, pupilHalf * 2f)
            )
        }
    }
}

@Composable
private fun ArcadeStatsCard(
    bestScore: Int,
    gamesPlayed: Int,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
    bestKills: Int? = null,
    bestLength: Int? = null,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(canvasColors.hudBgColor, RoundedCornerShape(8.dp))
            .border(1.dp, canvasColors.gridColor, RoundedCornerShape(8.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            StatItem(stringResource(Res.string.high_score_label), "$bestScore", canvasColors.accentColor)
            StatItem(stringResource(Res.string.games_played_label), "$gamesPlayed", canvasColors.hudTextColor)
        }
        if (bestKills != null && bestLength != null) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceEvenly
            ) {
                StatItem(stringResource(Res.string.best_kills_label), "$bestKills", canvasColors.hudTextColor)
                StatItem(stringResource(Res.string.best_length_label), "$bestLength", canvasColors.hudTextColor)
            }
        }
    }
}



@Composable
private fun StatItem(label: String, value: String, color: Color) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(text = label, color = color.copy(alpha = 0.75f), fontSize = 10.sp, fontFamily = appFontFamily(), fontWeight = FontWeight.Bold)
        Text(text = value, color = color, fontSize = 16.sp, fontFamily = appFontFamily(), fontWeight = FontWeight.Black)
    }
}

@Composable
private fun StartHeroButton(
    enabled: Boolean,
    onClick: () -> Unit,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
) {
    Button(
        onClick = onClick,
        enabled = enabled,
        colors = ButtonDefaults.buttonColors(
            containerColor = canvasColors.hudTextColor,
            contentColor = Color.Black,
            disabledContainerColor = canvasColors.hudTextColor.copy(alpha = 0.3f),
            disabledContentColor = Color.Black.copy(alpha = 0.5f)
        ),
        shape = RoundedCornerShape(8.dp),
        modifier = Modifier.fillMaxWidth().height(52.dp)
    ) {
        Text(
            text = stringResource(Res.string.start_game),
            color = Color.Black,
            fontSize = 18.sp,
            fontWeight = FontWeight.Black,
            fontFamily = appFontFamily(),
            letterSpacing = 2.sp
        )
    }
}

@Composable
private fun LeaderboardNavButton(
    onLeaderboard: () -> Unit,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
) {
    Button(
        onClick = onLeaderboard,
        colors = ButtonDefaults.buttonColors(
            containerColor = canvasColors.hudBgColor,
            contentColor = canvasColors.boundaryColor,
        ),
        shape = RoundedCornerShape(6.dp),
        modifier = Modifier.fillMaxWidth().border(1.dp, canvasColors.boundaryColor, RoundedCornerShape(6.dp))
    ) {
        Text(
            stringResource(Res.string.leaderboard),
            color = canvasColors.boundaryColor,
            fontSize = 12.sp,
            fontFamily = appFontFamily(),
            fontWeight = FontWeight.Bold,
        )
    }
}
